import { LineChartResultResponse } from '@business/entities/dashboard';
import { R } from '@core/utils/r';
import 'chartist/dist/scss/chartist.scss';
import React, { memo, useMemo, Fragment, useCallback } from 'react';
import { Card, CardTitle } from 'reactstrap';
import { WidgetProps } from '../types';
import './index.scss';
import { WidgetIcons } from '../BaseWidget';
import ReactEcharts, { EChartsOption } from 'echarts-for-react';
import { openGraphValueModal } from '@modules/common/AddEditGraphValueModal';
import { openGraphValuesOverlay } from '@modules/common/GraphValuesOverlay';
import { FormSelect } from '@core/components/form';
import { formControl } from '@core/utils/form';
import { GraphService, CommonService } from '@business/services';
import { GraphTypes } from '@business/entities/graph';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { StatChartWidgetConfigData } from './StatChartWidgetConfig';
import { toMoment } from '@core/utils/time';
import { useQueryStringParam } from '@core/router';

// import '../../assets/scss/chartist.scss';

export const StatChartWidget = memo((props: WidgetProps<LineChartResultResponse, StatChartWidgetConfigData>) => {
  const { settings, result, config } = props;
  const print = props.print || false;
  const timezone = useQueryStringParam('tz') || 'America/New_York';

  const control = useMemo(() => formControl<GraphTypes>({ initialValue: R.get(props, 'data.currentGraphType') }), [props]);

  const isUpsideDown = useMemo(() => {
    return config?.upside || false;
  }, [config]);

  const graphItems = useMemo(() => {
    return CommonService.selectItems(
      GraphService.getTypes(R.get(props, 'data.graphType')),
      i => R.capitalizeWords(i),
      i => i,
    );
  }, [props]);

  const statOptions = useMemo(() => {
    return {
      series: [
        {
          data: (result ? result.newValues : []).map((f: any) => ({ value: f })),
          type: 'custom',
          name: '',
          renderItem: function (_: any, api: any) {
            const start = api.coord([api.value(0), api.value(1)]);
            const end = api.coord([api.value(2), api.value(3)]);
            if ((R.isNaN(start[1]) || R.isNull(start[1])) && (R.isNaN(end[1]) || R.isNull(end[1]))) {
              return null;
            }
            return {
              type: 'group',
              children: [
                {
                  type: 'circle',
                  shape: {
                    cx: 0,
                    cy: 0,
                    r: print ? 6 : 3,
                  },
                  style: {
                    opacity: 1,
                    stroke: start[1] < end[1] ? 'red' : 'black',
                    lineWidth: 2,
                    fill: 'white',
                  },
                  x: start[0],
                  y: start[1],
                  z2: 1,
                },
                {
                  type: 'line',
                  shape: {
                    x1: start[0],
                    y1: start[1],
                    x2: end[0],
                    y2: end[1],
                  },
                  z2: 0,
                  style: {
                    lineWidth: print ? 4 : 2,
                    stroke: start[1] < end[1] ? 'red' : 'black',
                  },
                },
              ],
            };
          },
        },
      ],
      tooltip: {
        trigger: 'axis',
      },
      grid: {
        zlevel: 0,
        x: 50,
        x2: 50,
        y: 30,
        y2: 30,
        borderWidth: 0,
      },
      xAxis: {
        type: 'category',
        data: result ? result.labels : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        axisLabel: {},
      },
      yAxis: {
        inverse: isUpsideDown,
        type: 'value',
        min: function (value: any) {
          return value.min - 1;
        },
        max: function (value: any) {
          return value.max + 1;
        },
        axisLable: {
          color: '#ffffff',
        },
        axisLine: {
          lineStyle: {
            color: '#74788d',
          },
        },
      },
    };
  }, [isUpsideDown, print, result]);

  if (props.print) {
    const dateRange = props.dateRange!;
    const startDate = toMoment(R.toNumber(dateRange.split('-')[0])).tz(timezone);
    const endDate = toMoment(R.toNumber(dateRange.split('-')[1])).tz(timezone);

    return (
      <Card className="p-4" style={{ marginBottom: 0, flex: 1, textAlign: 'center' }}>
        <div className="mb-2 d-flex align-items-center font-weight-bold flex-row font-size-24 justify-content-center">
          {settings.title || 'Stat Graph'}
        </div>
        <div className="font-weight-medium font-size-16">
          {startDate.format('MMM D')} - {endDate.format('MMM D')}
        </div>
        <ReactEcharts
          style={{ height: '100%', width: '100%' }}
          option={{
            ...statOptions,
            grid: { ...statOptions.grid, bottom: 100, right: 50, left: 50 },
            xAxis: {
              ...statOptions.xAxis,
              axisLabel: {
                rotate: 45,
                margin: 10,
                formatter: function (value: any, index: number) {
                  return value + ' - ' + ((result as any)?.values[index] || 'NR');
                },
                textStyle: {
                  fontSize: 12,
                  fontWeight: 'bold',
                },
              },
              axisLine: {
                lineStyle: {
                  color: '#74788d',
                },
              },
            },
          }}
        />
      </Card>
    );
  }

  return (
    <Card className="p-4" style={{ marginBottom: 0, flex: 1 }}>
      <CardTitle className="mb-4 d-flex align-items-center flex-row">
        {settings.title || 'Stat Graph'}
        <WidgetIcons {...props} />
        {!props.print && graphItems.length > 1 && (
          <div className="ml-2" style={{ width: 150 }}>
            <FormSelect
              control={control}
              withoutGroup
              items={graphItems}
              onChange={type => props.onUpdateGraphType && props.onUpdateGraphType(type!)}
            />
          </div>
        )}
      </CardTitle>
      <ReactEcharts style={{ height: '100%', width: '100%' }} option={statOptions} />
    </Card>
  );
});
